/**
 * 편집기 탭 id — **모든 프로젝트·개별 스크립트를 통틀어 하나뿐**이어야 한다.
 *
 * 탭은 id 로 닫고·고르고·그린다(React key). 같은 id 가 둘이면 하나를 닫을 때 둘 다 닫히고(다른 프로젝트의 탭까지),
 * 그 프로젝트가 비면 프로젝트째 닫히며, 탭 줄에는 지워지지 않는 탭이 남는다 — 스탠드얼론에서 실제로 났다(2026-10-12):
 * 번호를 세는 카운터가 앱을 켤 때마다 2 부터 다시 시작해서, 복원된 탭(`new_2`, `new_3` …)과 같은 id 를 새 탭에 줬다.
 * 이름은 프로젝트 안에서만 겹치지 않게 보고 있어서 다른 프로젝트의 탭과 겹치는 것은 걸리지 않았다.
 *
 * 그래서 번호는 **지금 있는 탭에서** 정하고(`nextTabNumber`·`freeTabNumber`), 이미 겹친 채 저장된 상태는 복원할 때 고친다(`repairTabIds`).
 */

const NEW_RE = /^new_(\d+)$/;

/** 지금 탭들 다음에 쓸 번호 — `new_N` 의 가장 큰 N + 1 (없으면 2: 첫 탭이 new_1 이다) */
export function nextTabNumber(files) {
  let max = 1;
  for (const f of files || []) {
    const m = NEW_RE.exec(String((f && f.id) || ''));
    if (m) max = Math.max(max, parseInt(m[1], 10) || 0);
  }
  return max + 1;
}

/** start 부터 올라가며 `new_N` 이 쓰이지 않은 첫 번호 */
export function freeTabNumber(files, start = 2) {
  const used = new Set((files || []).map(f => f && f.id));
  let n = Math.max(1, Math.floor(Number(start)) || 1);
  while (used.has(`new_${n}`)) n += 1;
  return n;
}

/** base 가 쓰이고 있으면 뒤에 번호를 붙여 비킨다 — 파일에서 연 탭처럼 id 를 이름으로 짓는 자리 */
export function uniqueTabId(base, files) {
  const used = new Set((files || []).map(f => f && f.id));
  if (!used.has(base)) return base;
  let n = 2;
  while (used.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

/**
 * 겹친 id 를 고친다 — 앞의 탭은 그대로 두고(활성 탭·결과 탭이 가리키던 것), 뒤의 탭에 새 id 를 준다. id 가 없는 탭도 채운다.
 * 겹친 것이 없으면 **받은 배열을 그대로** 돌려준다(쓸데없는 리렌더·저장을 만들지 않는다).
 */
export function repairTabIds(files) {
  const list = Array.isArray(files) ? files : [];
  const seen = new Set();
  let dirty = false;
  for (const f of list) {
    const id = f && f.id;
    if (!id || seen.has(id)) { dirty = true; break; }
    seen.add(id);
  }
  if (!dirty) return list;
  const all = new Set(list.map(f => f && f.id).filter(Boolean));
  const kept = new Set();
  let n = nextTabNumber(list);
  return list.map((f) => {
    if (f && f.id && !kept.has(f.id)) { kept.add(f.id); return f; }
    while (all.has(`new_${n}`)) n += 1;
    const id = `new_${n}`;
    all.add(id); kept.add(id); n += 1;
    return { ...(f || {}), id };
  });
}
